// Doc 03 §3 — shared image presentation primitives for both template engines.
// `img` nodes carry `shape`, `ring`, `shadow`, and `cutout` props. Keeping the
// mapping here means the legacy and paged renderers stay byte-identical in look.

import type { CSSProperties } from "react";

export type ImageShape = "circle" | "rounded" | "square" | "plate" | "polaroid";

const SHAPE_RADIUS: Record<ImageShape, string> = {
  circle: "50%",
  rounded: "16px",
  square: "0",
  plate: "50%",
  polaroid: "2px",
};

const SHADOW_PRESETS: Record<string, string> = {
  sm: "0 2px 6px rgba(0,0,0,0.12)",
  md: "0 10px 24px rgba(0,0,0,0.18)",
  lg: "0 18px 40px rgba(0,0,0,0.24)",
};

function normalizeShape(value: unknown): ImageShape | undefined {
  if (typeof value !== "string") return undefined;
  const key = value.trim().toLowerCase();
  return key in SHAPE_RADIUS ? (key as ImageShape) : undefined;
}

function compositeShadow(existing: string | undefined, next: string | undefined): string | undefined {
  return [existing, next].filter(Boolean).join(", ") || undefined;
}

const DEFAULT_RING_COLOR = "var(--mnrq-color-accent, rgba(0,0,0,0.14))";

/**
 * A ring is a solid outline drawn with `box-shadow`. Templates may pass:
 *   · `true`                → 3px accent ring
 *   · `"#f7af2a"`           → 3px ring in that colour
 *   · `"8px var(--mnrq-…)"` → 8px ring in that colour
 * The leading-length form exists because doc 03a measures rings in px (e.g.
 * Spotlight Poster's 8px accent ring); previously any non-boolean string was
 * treated as a bare colour, so "8px …" produced an invalid shadow.
 */
export function resolveRingShadow(ring: unknown): string | undefined {
  if (ring === true) return `0 0 0 3px ${DEFAULT_RING_COLOR}`;
  if (typeof ring !== "string") return undefined;
  const trimmed = ring.trim();
  if (!trimmed || trimmed === "true") return `0 0 0 3px ${DEFAULT_RING_COLOR}`;
  const withWidth = trimmed.match(/^(\d+(?:\.\d+)?(?:px|rem|em)?)\s+(.+)$/);
  if (withWidth) return `0 0 0 ${withWidth[1]} ${withWidth[2]}`;
  return `0 0 0 3px ${trimmed}`;
}

export interface ImagePresentation {
  style: CSSProperties;
  className?: string;
}

/**
 * Resolve presentational CSS for an image node. `shape` sets the radius (and a
 * plate/polaroid frame); `ring` draws an accent-coloured outline; `shadow`
 * applies a preset or the default elevation.
 */
export function resolveImagePresentation(props: {
  shape?: unknown;
  ring?: unknown;
  shadow?: unknown;
}): ImagePresentation {
  const shape = normalizeShape(props.shape);
  const style: CSSProperties = {};
  const className: string[] = [];

  if (shape) {
    style.borderRadius = SHAPE_RADIUS[shape];
    if (shape === "plate") {
      style.border = "6px solid rgba(255,255,255,0.9)";
      style.boxShadow = "0 10px 24px rgba(0,0,0,0.18)";
    } else if (shape === "polaroid") {
      style.border = "10px solid var(--mnrq-color-surface, white)";
      style.borderBottomWidth = "28px";
      style.background = "var(--mnrq-color-surface, white)";
      style.boxShadow = "0 8px 20px rgba(0,0,0,0.18)";
    }
  }

  if (props.ring) {
    // The renderer injects the template palette as --mnrq-color-* custom
    // properties (buildTemplateCssVars), which is why the default ring colour
    // is a var() rather than a literal.
    style.boxShadow = compositeShadow(style.boxShadow, resolveRingShadow(props.ring));
  }

  if (props.shadow) {
    const preset =
      typeof props.shadow === "string" && props.shadow !== "true"
        ? SHADOW_PRESETS[props.shadow] || props.shadow
        : SHADOW_PRESETS.md;
    style.boxShadow = compositeShadow(style.boxShadow, preset);
  }

  return { style, className: className.join(" ") || undefined };
}

export type CutoutMode = "prefer" | "require" | "never";

export interface CutoutResolution {
  src: string | undefined;
  shapeOverride?: ImageShape;
}

/**
 * Pick the source for an `img` node given its `cutout` mode. A cut-out is only
 * used when the item actually has one (`image_cutout_url` exposed by
 * menuDataBuilder once the background-removal job is `ready`). `require` with no
 * cut-out falls back to the original photo as a circle (doc 03 §3) — the photo
 * of record is never replaced.
 */
export function resolveCutoutSource(
  mode: unknown,
  entry: Record<string, unknown> | null | undefined,
  fallbackSrc: string | undefined,
): CutoutResolution {
  if (typeof mode !== "string") return { src: fallbackSrc };
  const normalized = mode.trim().toLowerCase();
  if (normalized !== "prefer" && normalized !== "require" && normalized !== "never") {
    return { src: fallbackSrc };
  }
  if (normalized === "never") return { src: fallbackSrc };

  const cutout = typeof entry?.image_cutout_url === "string" ? entry.image_cutout_url.trim() : "";
  if (cutout) return { src: cutout };
  return normalized === "require" ? { src: fallbackSrc, shapeOverride: "circle" } : { src: fallbackSrc };
}

/**
 * Monogram fallback for an `img` node with no resolvable photo. Doc 03 §2
 * requires "items without photos" to render a monogram disc in the accent
 * colour — never a grey box — so the initial is derived from the item/category
 * name and the renderer draws a disc instead of an empty `<img>`.
 */
export function resolveImageFallbackInitial(entry: Record<string, unknown> | null | undefined): string {
  const name = String(entry?.title ?? entry?.name ?? "").trim();
  return name ? name[0].toUpperCase() : "?";
}

/** CSS for the monogram disc that stands in for a missing photo. */
export function resolveImageFallbackPresentation(shape?: unknown): { style: CSSProperties; initial: string } {
  const presentation = resolveImagePresentation({ shape: shape ?? "circle" });
  return {
    style: {
      ...presentation.style,
      background: "var(--mnrq-color-accent, var(--primary, white))",
      color: "var(--mnrq-color-accent-foreground, var(--primary-foreground, white))",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      aspectRatio: "1 / 1",
    },
    initial: "?",
  };
}

/**
 * Decorative assets (leaves, arrows, bursts, kente bands) live in the
 * `template-assets` bucket of the **Storage** project. A bare key such as
 * `seasonal/leaf-3.svg` is resolved to a public URL; absolute/data/relative
 * paths pass through unchanged. Colourising inline SVGs is not handled here —
 * templates that need a recolourable asset should inline it.
 */
export function resolveDecorAssetUrl(value: unknown, bucket = "template-assets"): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^(https?:|data:|blob:)/i.test(trimmed) || trimmed.startsWith("/")) return trimmed;
  const base = process.env.NEXT_PUBLIC_SUPABASE_STORAGE_URL?.replace(/\/+$/, "");
  if (!base) return trimmed;
  const key = trimmed.replace(/^\/+/, "").replace(new RegExp(`^${bucket}/+`), "");
  return `${base}/storage/v1/object/public/${bucket}/${encodeURI(key)}`;
}
